import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, TextField, MenuItem, Grid, Button, Chip,
    FormControl, InputLabel, Select, OutlinedInput, Checkbox, ListItemText, Divider
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import * as actionTypes from '../../redux/actionTypes';

const TYPES = ['Bug', 'Feature Request', 'Enhancement', 'Crash', 'UI Problem'];
const MODULES = ['Recharge', 'Flight', 'Hotel', 'Kundli', 'Milan Sathi', 'Yatra', 'Sanchar', 'Wallet', 'Auth', 'Home', 'Astrologer', 'E-Commerce', 'Admin Panel', 'Backend', 'Other'];
const PLATFORMS = ['Android', 'iOS', 'Website', 'Admin', 'Vendor', 'Backend'];
const FREQUENCIES = ['Always', 'Sometimes', 'Rare'];

const CreateIssue = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        title: '',
        type: '',
        module: '',
        platform: [],
        description: '',
        expectedResult: '',
        steps: '',
        frequency: '',
        reproducible: 'Yes',
        deviceInfo: { deviceName: '', osVersion: '', appVersion: '', browser: '' },
        createdBy: '',
    });

    const [errors, setErrors] = useState({});

    const validate = () => {
        const errs = {};
        if (!formData.title.trim()) errs.title = 'Title is required';
        if (!formData.type) errs.type = 'Type is required';
        if (!formData.module) errs.module = 'Module is required';
        if (formData.platform.length === 0) errs.platform = 'Select at least one platform';
        if (!formData.description.trim()) errs.description = 'Description is required';
        if (!formData.createdBy.trim()) errs.createdBy = 'Creator name is required';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const handleDeviceInfoChange = (field, value) => {
        setFormData(prev => ({ ...prev, deviceInfo: { ...prev.deviceInfo, [field]: value } }));
    };

    const handleSubmit = () => {
        if (!validate()) return;
        const payload = { ...formData };
        if (payload.steps) payload.steps = payload.steps.split('\n').filter(s => s.trim());

        dispatch({
            type: actionTypes.CREATE_ISSUE,
            payload: {
                data: payload,
                onComplete: () => navigate('/issues'),
            }
        });
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ borderRadius: 2, textTransform: 'none' }}>Back</Button>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>🆕 Create New Issue</Typography>
            </Box>

            {/* Section 1: Basic Info */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>📝 Basic Information</Typography>
                <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Issue Title *" value={formData.title} onChange={(e) => handleChange('title', e.target.value)}
                            error={!!errors.title} helperText={errors.title} placeholder="Brief, clear title describing the issue" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth select label="Issue Type *" value={formData.type} onChange={(e) => handleChange('type', e.target.value)}
                            error={!!errors.type} helperText={errors.type}>
                            {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth select label="Module *" value={formData.module} onChange={(e) => handleChange('module', e.target.value)}
                            error={!!errors.module} helperText={errors.module}>
                            {MODULES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth error={!!errors.platform}>
                            <InputLabel>Platform *</InputLabel>
                            <Select multiple value={formData.platform} onChange={(e) => handleChange('platform', e.target.value)}
                                input={<OutlinedInput label="Platform *" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {selected.map(v => <Chip key={v} label={v} size="small" />)}
                                    </Box>
                                )}>
                                {PLATFORMS.map(p => (
                                    <MenuItem key={p} value={p}>
                                        <Checkbox checked={formData.platform.indexOf(p) > -1} size="small" />
                                        <ListItemText primary={p} />
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.platform && <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>{errors.platform}</Typography>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Created By *" value={formData.createdBy} onChange={(e) => handleChange('createdBy', e.target.value)}
                            error={!!errors.createdBy} helperText={errors.createdBy} placeholder="Your name" />
                    </Grid>
                </Grid>
            </Paper>

            {/* Section 2: Description */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#FF9800' }}>📋 Description & Steps</Typography>
                <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                        <TextField fullWidth multiline rows={4} label="Issue Description *" value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)} error={!!errors.description} helperText={errors.description}
                            placeholder="Detailed description of the issue..." />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth multiline rows={3} label="Expected Result" value={formData.expectedResult}
                            onChange={(e) => handleChange('expectedResult', e.target.value)}
                            placeholder="What should happen instead?" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth multiline rows={4} label="Steps to Reproduce (one per line)" value={formData.steps}
                            onChange={(e) => handleChange('steps', e.target.value)}
                            placeholder="Step 1: Open the app&#10;Step 2: Go to Recharge section&#10;Step 3: Select operator&#10;Step 4: Error appears" />
                    </Grid>
                </Grid>
            </Paper>

            {/* Section 3: Reproducibility */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#9C27B0' }}>🔄 Reproducibility</Typography>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth select label="Frequency" value={formData.frequency} onChange={(e) => handleChange('frequency', e.target.value)}>
                            {FREQUENCIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth select label="Reproducible?" value={formData.reproducible} onChange={(e) => handleChange('reproducible', e.target.value)}>
                            <MenuItem value="Yes">Yes - Can be reproduced</MenuItem>
                            <MenuItem value="No">No - Random/Intermittent</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {/* Section 4: Device Info */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#4CAF50' }}>📱 Device Information (Optional)</Typography>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={3}>
                        <TextField fullWidth label="Device" value={formData.deviceInfo.deviceName} onChange={(e) => handleDeviceInfoChange('deviceName', e.target.value)} placeholder="Samsung Galaxy S21" />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField fullWidth label="OS" value={formData.deviceInfo.osVersion} onChange={(e) => handleDeviceInfoChange('osVersion', e.target.value)} placeholder="Android 13" />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField fullWidth label="App Version" value={formData.deviceInfo.appVersion} onChange={(e) => handleDeviceInfoChange('appVersion', e.target.value)} placeholder="2.20" />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField fullWidth label="Browser" value={formData.deviceInfo.browser} onChange={(e) => handleDeviceInfoChange('browser', e.target.value)} placeholder="Chrome / Safari" />
                    </Grid>
                </Grid>
            </Paper>

            <Divider sx={{ my: 2 }} />

            {/* Submit */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={() => navigate(-1)} sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}>Cancel</Button>
                <Button variant="contained" startIcon={<Save />} onClick={handleSubmit}
                    sx={{ borderRadius: 2, textTransform: 'none', px: 4, fontWeight: 600, bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>
                    Create Issue
                </Button>
            </Box>
        </Box>
    );
};

export default CreateIssue;
